import { useCallback } from 'react';

import { useSsoPageEmailDomainExclusivitySetMutation } from '../../../generated';
import type { SsoEmailDomainExclusivityResult } from '../types';
import { mapToSsoEmailDomain } from '../utils';

export interface SetSsoEmailDomainExclusivityInput {
  connectionId: string;
  /** Cognito's spelling. The backend trims and lower-cases it before storing. */
  emailDomain: string;
  exclusiveFederatedLogin: boolean;
}

export interface UseSsoEmailDomainExclusivityResult {
  setExclusivity: (
    input: SetSsoEmailDomainExclusivityInput
  ) => Promise<SsoEmailDomainExclusivityResult>;
  isSaving: boolean;
}

/**
 * Sets or clears the federated-only flag on one email domain.
 *
 * The two directions are not symmetric. Turning it on requires a domain Cognito
 * routes to this connection, so anything else comes back as an invalid
 * `emailDomain`. Turning it off deletes the row and always succeeds, which is
 * what lets a domain Cognito no longer routes still be cleared.
 *
 * No refetch runs. The flag is read through a secondary index, so a read taken
 * straight after the write can still answer with the old value and would undo
 * what the person just did on screen. The caller holds the new value instead.
 */
export const useSsoEmailDomainExclusivity =
  (): UseSsoEmailDomainExclusivityResult => {
    const [mutate, { loading }] = useSsoPageEmailDomainExclusivitySetMutation();

    const setExclusivity = useCallback(
      async ({
        connectionId,
        emailDomain,
        exclusiveFederatedLogin,
      }: SetSsoEmailDomainExclusivityInput): Promise<SsoEmailDomainExclusivityResult> => {
        const result = await mutate({
          variables: {
            input: { connectionId, emailDomain, exclusiveFederatedLogin },
          },
        });

        const [transportError] = result.errors ?? [];
        const response = result.data?.ssoEmailDomainExclusivitySet;

        if (!response) {
          return {
            kind: 'request',
            message:
              transportError?.message ?? 'The domain could not be changed.',
          };
        }

        switch (response.__typename) {
          case 'SsoEmailDomain':
            return {
              kind: 'success',
              emailDomain: mapToSsoEmailDomain(response),
            };
          case 'NotFoundError':
            return {
              kind: 'notFound',
              message: response.message ?? 'The connection no longer exists.',
            };
          case 'InputValidationFailure':
            return {
              kind: 'validation',
              message: response.message ?? 'The domain could not be changed.',
              invalidFields:
                response.invalidFields?.map((field) => ({
                  fieldName: field.fieldName,
                  reason: field.reason,
                })) ?? [],
            };
          case 'AuthorizationFailure':
            return {
              kind: 'authorization',
              message:
                response.message ?? 'Not authorized to change email domains.',
            };
          default:
            return {
              kind: 'request',
              message: 'The domain could not be changed.',
            };
        }
      },
      [mutate]
    );

    return { setExclusivity, isSaving: loading };
  };
